import { ThemeProvider } from '@emotion/react';
import { Alert, Checkbox, FormControlLabel } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Input from '@mui/material/Input';
import Slider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import { createTheme, styled } from '@mui/material/styles';
import {
  createNft,
  uploadFileWatermark,
  uploadIpfsFile,
} from 'APIs/HotAuctionApi';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import NormalButton from 'components/atoms/Button/NormalButton';
import DropZoneCards from 'components/molecules/Card/DropZone/DropZoneCards';
import Categories from 'components/molecules/CategoryModal/Categories';
import UploadNft from 'components/molecules/CreateNftForm/UploadNft';
import InputText from 'components/molecules/InputText/InputText';
import { selectAuth } from 'features/auth/authSlice';
import { getNfts, selectNft } from 'features/auth/nftSlice';
import { IpfsFileUploadData } from 'interfaces/IpfsFileUploadData.interface';
import { INftForm, NftType } from 'interfaces/NftData.interface';
import * as React from 'react';
import { useSelector } from 'react-redux';
import classes from './creationBody.module.css';

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
    secondary: {
      main: '#36899f',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

const WhiteText = styled(Input)(() => ({
  '& .MuiInputBase-input': {
    color: '#fff', // Text color
    fontSize: '14px',
  },
}));

const AirbnbSlider = styled(Slider)(({ theme }) => ({
  color: '#3a8589',
  height: 3,
  padding: '13px 0',
  '& .MuiSlider-thumb': {
    height: 27,
    width: 27,
    border: '1px solid currentColor',
    '&:hover': {
      boxShadow: '0 0 0 8px rgba(58, 133, 137, 0.16)',
    },
    '& .airbnb-bar': {
      height: 9,
      width: 1,
      backgroundColor: 'currentColor',
      marginLeft: 1,
      marginRight: 1,
    },
  },
  '& .MuiSlider-track': {
    height: 3,
  },
  '& .MuiSlider-rail': {
    color: theme.palette.mode === 'dark' ? '#bfbfbf' : '#d8d8d8',
    opacity: theme.palette.mode === 'dark' ? undefined : 1,
    height: 3,
  },
}));

type OwnAlt = {
  yes: boolean;
  no: boolean;
};
const CreationBody = () => {
  const [NFT, setNFT] = React.useState<number>(30);
  const [success, setSuccess] = React.useState<boolean>(false);
  const [failure, setFailure] = React.useState<boolean>(false);
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [badFile, setBadFile] = React.useState<boolean>(false);
  const [uploadPercentage, setUploadPercentage] = React.useState<number>(0);
  const [categories, setCategories] = React.useState<NftType[]>([]);
  const dispatch = useAppDispatch();

  const nftState = useSelector(selectNft);
  const [ownData, setOwnData] = React.useState<OwnAlt>({
    yes: true,
    no: false,
  });
  const [form, setForm] = React.useState<INftForm>({
    title: '',
    description: '',
    image: undefined,
    username: undefined,
    isVertical: true,
    watermark: false,
    watermarkSrc: undefined,
    categories: undefined,
    reservedBid: 0,
    ipfsMetaDataUrl: '',
    ipfsVideoUrl: '',
  });

  const authState = useAppSelector(selectAuth);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.currentTarget;
    if (name !== 'image') {
      setForm({ ...form, [name]: value });
    } else {
      if (files) setForm({ ...form, [name]: files[0] });
    }
  };

  const setYesTrue = (): void => {
    setOwnData({ yes: true, no: false });
  };
  const setNoTrue = (): void => {
    setOwnData({ yes: false, no: true });
  };

  const emptyFunc = () => {};

  const handleNFTSliderChange = (newNFT: number): void => {
    setNFT(newNFT);
  };

  const handleNFTInputChange = (event: any): void => {
    setNFT(event.target.NFT);
  };

  const handleNFTBlur = (): void => {
    if (NFT < 0) {
      setNFT(0);
    } else if (NFT > 100) {
      setNFT(100);
    }
  };

  const getFiles = (file: File) => {
    setForm({
      ...form,
      image: file,
    });
  };

  const onNftSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (form.image && authState.username && form.reservedBid > 0) {
      try {
        let ipfsFileUpload: IpfsFileUploadData = {
          videoUrl: '',
          metaDataUrl: '',
        };
        const videoDiv = document.getElementById('video');

        setUploading(true);

        form.username = authState.username;
        form.categories = categories;
        if (authState.tatumWalletAddress) {
          const ipfsFormData = new FormData();
          ipfsFormData.append('file', form.image);
          ipfsFormData.append('title', form.title);
          ipfsFormData.append('description', form.description);
          const ipfsFileUploadRespone = await uploadIpfsFile(ipfsFormData);
          ipfsFileUpload = ipfsFileUploadRespone.data;
          // if (ipfsFileUpload) {
          // const mintNftResponse = await mintNftTatum(
          //   authState.tatumWalletAddress,
          //   ipfsFileUpload.metaDataUrl
          // );
          // const mintNftOnTatumData = mintNftResponse.data;
          // if (mintNftOnTatumData) {
          form.ipfsMetaDataUrl = ipfsFileUpload.metaDataUrl;
          form.ipfsVideoUrl = ipfsFileUpload.videoUrl;
          // }

          // await addAddressAsNftMinter();
          // const getAddressAndTokenId = await getNftAddressAndTokenId(
          //   authState.tatumWalletAddress
          // );
          // const getAddressAndTokenIdData = getAddressAndTokenId.data;
          // form.tokenId = getAddressAndTokenIdData.tokenId;
          // form.contractAddress = getAddressAndTokenIdData.contractAddress;
          // }
          if (form.watermark) {
            const watermarkFormdata = new FormData();
            watermarkFormdata.append('file', form.image);
            if (videoDiv?.videoWidth && videoDiv?.videoHeight) {
              watermarkFormdata.append(
                'videoHeight',
                videoDiv?.videoHeight.toString()
              );
              watermarkFormdata.append(
                'videoWidth',
                videoDiv?.videoWidth.toString()
              );
              const watermarkFileUpload = await uploadFileWatermark(
                watermarkFormdata
              );
              form.watermarkSrc = watermarkFileUpload.data;
            }
          }
        }
        form.image = ipfsFileUpload.videoUrl;

        const uploadNft = await createNft(form);

        setUploadPercentage(0);
        dispatch(getNfts());
        if (uploadNft?.data && !nftState.loading) {
          setUploading(false);
          setSuccess(true);
          setFailure(false);
          setBadFile(false);
        }
      } catch (error) {
        console.log('error', error);
        setUploading(false);
        setFailure(true);
        setBadFile(false);
        setSuccess(false);
        setUploadPercentage(0);
      }
    } else {
      setUploading(false);
      setFailure(true);
      setBadFile(false);
      setSuccess(false);
      setUploadPercentage(0);
    }
  };

  return (
    <React.Fragment>
      <form onSubmit={onNftSubmit}>
        {uploading ? (
          <UploadNft
            value={uploadPercentage}
            description="Our hamster's are working really hard to get your content on the explore
          page."
            uploadingItem="Collectible"
          />
        ) : (
          <ThemeProvider theme={colortheme}>
            <Grid container className={classes.body}>
              {success && (
                <Alert
                  sx={{ width: '100%', marginBottom: '20px' }}
                  onClose={() => setSuccess(false)}
                  severity="success"
                >
                  Your Nft has been uploaded successfully.
                </Alert>
              )}
              {failure && (
                <Alert
                  sx={{ width: '100%', marginBottom: '20px' }}
                  onClose={() => setFailure(false)}
                  severity="error"
                >
                  There was an error uploading your Nft.
                </Alert>
              )}
              {badFile && (
                <Alert
                  sx={{ width: '100%', marginBottom: '20px' }}
                  onClose={() => setBadFile(false)}
                  severity="error"
                >
                  Please upload a file smaller than 3MB.
                </Alert>
              )}
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '0px',
                    fontSize: '2rem',
                  }}
                >
                  Create Collectable Form
                </Typography>
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '0px',
                  }}
                >
                  Name
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <InputText
                  onChange={handleChange}
                  initValue={'Item Name'}
                  type={'bookmark'}
                  name="title"
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography className={classes.titleText}>
                  Description
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Tell us a little bit about your Collectable.
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <InputText
                  onChange={handleChange}
                  initValue={'Description.....'}
                  type={'grid'}
                  name="description"
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography className={classes.titleText}>
                  Reserve Price
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Add a price for users to reserve your Collectable.
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <InputText
                  onChange={handleChange}
                  initValue={'Reserve price'}
                  type={'number'}
                  name="reservedBid"
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography className={classes.titleText}>Watermark</Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Do you want to allow viewers ability to download this
                  collectable with a watermark? (This is great for allowing
                  sharing on other social media platforms, but maintaining
                  certainty that others will know where the video came from with
                  your username attached).
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <FormControlLabel
                  sx={{ color: 'white' }}
                  control={
                    <Checkbox
                      color="secondary"
                      onChange={e =>
                        setForm({ ...form, watermark: e.target.checked })
                      }
                    />
                  }
                  label="Add Watermark"
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '50px',
                  }}
                >
                  Add your content
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Files types supported: JPG, PNG, GIF, SVG, MP4, WEBM, MP3,
                  WAV, OGG, GLB, GLTF. Max size: 100 MB
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <DropZoneCards
                  form={form}
                  setForm={setForm}
                  getFiles={getFiles}
                  onChange={handleChange}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '34px',
                  }}
                >
                  Do you own this audio?
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Disclaimer: must not contain any copyright audio or the
                  Collectable will be taken down.
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12} gap={2}>
                <div onClick={() => setYesTrue()}>
                  <NormalButton
                    text={'Yes'}
                    color={'yellow'}
                    isClicked={ownData.yes}
                  />
                </div>
                <div onClick={() => setNoTrue()}>
                  <NormalButton
                    text={'No'}
                    color={'yellow'}
                    isClicked={ownData.no}
                  />
                </div>
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography className={classes.titleText}>
                  Collectable Royalties
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Collectors may re-sell your Collectable, and you will receive
                  royalties for every time this Collectable is sold.
                </Typography>
              </Grid>
              <Grid
                className={classes.contentGrid}
                item
                xs={12}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <AirbnbSlider
                  value={typeof NFT === 'number' ? NFT : 0}
                  onChange={(e, v) => handleNFTSliderChange(v as number)}
                  aria-labelledby="input-slider"
                  color="secondary"
                />
                <WhiteText
                  disableUnderline
                  sx={{
                    width: '40px',
                    alignSelf: 'self-end',
                    color: '#c1c1c1',
                  }}
                  value={NFT}
                  size="small"
                  onChange={handleNFTInputChange}
                  onBlur={handleNFTBlur}
                  inputProps={{
                    step: 10,
                    min: 0,
                    max: 100,
                    type: 'number',
                    'aria-labelledby': 'input-slider',
                  }}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography className={classes.titleText}>
                  External Link
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  You can include a link on this item’s detail page, so that
                  users can click to learn more about it. You are welcome to
                  link to your own webpage with more details.
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <InputText
                  onChange={handleChange}
                  initValue={'https://yourwebsite.com/item/whatever'}
                  type={'launch'}
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '50px',
                  }}
                >
                  Set Categories For Your Collectable
                </Typography>
              </Grid>
              <Categories
                categories={categories}
                setCategories={setCategories}
              />
              <Grid
                className={classes.contentGrid}
                style={{ width: '100%', marginTop: '40px' }}
              >
                <Button
                  type="submit"
                  onClick={onNftSubmit}
                  className={classes.createButton}
                >
                  Create Collectable
                </Button>
              </Grid>
            </Grid>
          </ThemeProvider>
        )}
      </form>
    </React.Fragment>
  );
};

export default CreationBody;
